/* eslint-disable react/prop-types */

import { Row, Col, Image } from 'antd'

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
} from 'recharts'

import './style.less'
import '../ContentComponent/ContentComponent.style.less'
import moment from 'moment'
import { useEffect, useState } from 'react'
import transformDataChart from '../../Production.transform'
import { sortBy } from 'lodash'

function Legend({ color, label }: any) {
  return (
    <div className="ps-chart-legend-container">
      <div style={{ backgroundColor: color }} className="dot" />
      <p>{label}</p>
    </div>
  )
}

function ChartComponent(props: any) {
  const [chartData, setChartData] = useState(null)
  const { datesHeader, data } = props

  useEffect(() => {
    if (data) {
      setChartData(transformDataChart(data))
    }
  }, [data])

  function CustomTooltip({ active, payload }: any) {
    const sortedItems = (tmp: any) =>
      sortBy(tmp, (item) => {
        return ['plan', 'actual', 'pack']?.indexOf(item.name)
      })
    const newPayload = sortedItems(payload)

    const colorByName = (name) => {
      return name == 'plan'
        ? '#848484'
        : name == 'actual'
        ? '#65BF73'
        : '#4A8DC0'
    }

    const labelByName = (name) => {
      return name == 'plan' ? 'Planned' : name == 'actual' ? 'Actual' : 'Packed'
    }

    const totalByName = (name) => {
      return name == 'plan'
        ? 'Total Planned'
        : name == 'actual'
        ? 'Total Actual'
        : 'Total Packed'
    }

    function ToolTipComponent({ item }: any) {
      if (item?.name === 'pack' && item?.value === 0) {
        return null
      }
      return (
        <div className="main-container">
          <Col>
            <div
              className="dot"
              style={{ backgroundColor: colorByName(item?.name) }}
            />
          </Col>
          <div>
            <Row>
              <p className="label-name">{labelByName(item?.name)}:</p>
              <p className="value">{item?.payload?.totals[item?.name]}</p>
            </Row>
            <Row>
              <p className="label-name">{totalByName(item?.name)}:</p>
              <p className="value">{item?.value}</p>
            </Row>
            <Row style={{ margin: '4px 0px' }}>
              <Image
                className="image"
                src={item?.payload?.user[item?.name]?.profile}
              />
              <p className="value">{item?.payload?.user[item?.name]?.name}</p>
            </Row>
            <p className="date">
              {item?.payload?.last_modified_dates[item?.name]}
            </p>
          </div>
        </div>
      )
    }

    if (active && payload && payload?.length) {
      return (
        <div className="ps-chart-tooltip-container">
          <div>
            {newPayload?.map((item) => {
              return <ToolTipComponent key={item} item={item} />
            })}
          </div>
        </div>
      )
    }
    return null
  }

  const hightlightColor = (date) => {
    if (moment(date).startOf('day').isSame(moment().startOf('day'))) {
      return '#FDF5ED'
    } else if (
      moment(date, 'YYYY-MM-DD')
        .startOf('day')
        .isSame(moment(props.planEndDate, 'YYYY-MM-DD').startOf('day'))
    ) {
      return '#4a8dc033'
    }

    return '#fff'
  }

  function DatesHightLight() {
    return (
      <Row
        className="table-header-row"
        style={{
          position: 'absolute',
          zIndex: 0,
          marginLeft: 80,
          width: 'calc(100% - 80px)',
          marginTop: 15,
        }}
      >
        <Col span={24} className="highlight-date">
          {datesHeader?.map((item) => {
            return (
              <div
                className="cell"
                key={item?.dataIndex}
                style={{
                  backgroundColor: hightlightColor(item?.value),
                }}
              >
                <p className="bold">{item?.title}</p>
              </div>
            )
          })}
        </Col>
      </Row>
    )
  }

  return (
    <Row>
      <Col span={3} className="col-legend-container">
        <div>
          <Legend color="#848484" label="Production Planned" />
          <Legend color="#65BF73" label="Production Actual" />
          <Legend color="#4A8DC0" label="Packed Actual" />
        </div>
      </Col>
      <Col span={21}>
        <div
          className="table-content"
          style={{
            borderWidth: 0,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Row
            className="table-header-row"
            style={{
              position: 'absolute',
              zIndex: 100,
              marginLeft: 80,
              width: 'calc(100% - 80px)',
            }}
          >
            <Col span={24} className="header-date">
              {datesHeader?.map((item) => {
                return (
                  <div className="cell" key={item?.dataIndex}>
                    <p className="bold">{item?.title}</p>
                  </div>
                )
              })}
            </Col>
          </Row>
          <DatesHightLight />

          <Row>
            <Col span={24}>
              <ResponsiveContainer minWidth={500} minHeight={600}>
                <LineChart
                  data={chartData}
                  margin={{ top: 130, right: 0, left: 20, bottom: 8 }}
                >
                  <CartesianGrid horizontal={true} vertical={false} />

                  <YAxis type="number" axisLine={{ stroke: '#f6f6f6' }} />
                  <XAxis
                    dataKey="name"
                    orientation="top"
                    padding={{ left: 120, right: 120 }}
                    height={0}
                    stroke="#f6f6f6"
                  />
                  <Tooltip
                    content={CustomTooltip}
                    cursor={{ fill: 'transparent' }}
                  />
                  <ReferenceLine y={0} stroke="#f6f6f6" />
                  <Line
                    type="monotone"
                    dataKey="pack"
                    dot={{ r: 6, fill: '#4A8DC0' }}
                    stroke="#4A8DC0"
                    activeDot={{ r: 8 }}
                    animationDuration={100}
                    animationBegin={0}
                  />
                  <Line
                    type="monotone"
                    dataKey="plan"
                    stroke="#848484"
                    dot={{ r: 6, fill: '#848484' }}
                    activeDot={{ r: 8 }}
                    animationDuration={100}
                    animationBegin={0}
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    dot={{ r: 6, fill: '#65BF73' }}
                    stroke="#65BF73"
                    activeDot={{ r: 8 }}
                    animationBegin={0}
                    animationDuration={100}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  )
}

export default ChartComponent
